import React, { useEffect, useState } from 'react'
import { Card, Rate, Button, Avatar, Space, Tag, Typography, Spin, Row, Col, theme } from 'antd'
import {
  UserOutlined,
  EnvironmentOutlined,
  CalendarOutlined,
  ThunderboltOutlined,
  MessageOutlined,
  PhoneOutlined,
  VideoCameraOutlined,
  HomeOutlined,
  EyeOutlined
} from '@ant-design/icons'
import PlaceholderImage from '../../../assets/images/MH-logo-v1 1.png'

const { Text, Title } = Typography

const SearchResults = ({ results = [], loading = false, onViewAvailability, searchFilters = {}, searchType }) => {
  console.log('Search Filters', searchFilters)
  const [showResults, setShowResults] = useState(!loading) // Start with true if not loading initially
  const [loadingStartTime, setLoadingStartTime] = useState(null)

  // Helper function to format filter values with proper case
  const formatFilterValue = (filterType, value) => {
    const formatters = {
      consultType: {
        inPerson: 'In-Person',
        virtual: 'Virtual',
        homeVisit: 'Home Visit'
      },
      timeSlot: {
        morning: 'Morning',
        afternoon: 'Afternoon',
        evening: 'Evening'
      },
      languages: {
        english: 'English',
        afrikaans: 'Afrikaans',
        zulu: 'Zulu',
        xhosa: 'Xhosa',
        sotho: 'Sotho'
      },
      gender: {
        male: 'Male Doctor',
        female: 'Female Doctor',
        any: 'No Preference'
      }
    }

    return formatters[filterType]?.[value] || value
  }

  // Helper function to get filter category colors
  const getFilterColor = (filterType, value) => {
    const colors = {
      consultType: {
        inPerson: '#52c41a',
        virtual: '#1890ff',
        homeVisit: '#fa8c16'
      },
      timeSlot: '#fa8c16', // orange/warning color
      languages: '#52c41a', // green/success color
      gender: '#1890ff' // blue/info color
    }

    if (filterType === 'consultType') {
      return colors.consultType[value] || '#1890ff'
    }

    return colors[filterType] || '#1890ff'
  }

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  // Track loading start time
  useEffect(() => {
    if (loading) {
      setLoadingStartTime(Date.now())
      setShowResults(false)
    } else if (loadingStartTime) {
      // Ensure minimum loading time of 2 seconds for full animation
      const elapsed = Date.now() - loadingStartTime
      const minimumLoadingTime = 2000 // 2 seconds

      if (elapsed < minimumLoadingTime) {
        const remainingTime = minimumLoadingTime - elapsed
        setTimeout(() => {
          setShowResults(true)
        }, remainingTime)
      } else {
        setShowResults(true)
      }
    }
  }, [loading, loadingStartTime])

  const { token } = theme.useToken()
  const handleViewProfile = (doctor) => {
    onViewAvailability?.(doctor)
  }

  // This function is no longer needed as booking is handled by parent
  // const handleBookAppointment = (appointmentData) => {
  //     // }

  // Mock data structure matching your actual doctor records

  const displayResults = results.length > 0 ? results : []

  // Helper function to get doctor's full name
  const getDoctorName = (doctor) => {
    return `${doctor.firstName} ${doctor.lastName}`.trim()
  }

  // Helper function to check if doctor is active
  const isDoctorActive = (doctor) => {
    return doctor.extraData?.isActive !== false
  }

  // Helper function to format next available slot
  const formatNextAvailableSlot = (nextAvailableSlot) => {
    if (!nextAvailableSlot || !nextAvailableSlot.date || !nextAvailableSlot.timeSlot) {
      return 'No availability'
    }

    const { date, timeSlot } = nextAvailableSlot
    const slotDate = new Date(date)
    const today = new Date()
    const tomorrow = new Date(today)
    tomorrow.setDate(today.getDate() + 1)

    // Format the date
    let dateText = ''
    if (slotDate.toDateString() === today.toDateString()) {
      dateText = 'today'
    } else if (slotDate.toDateString() === tomorrow.toDateString()) {
      dateText = 'tomorrow'
    } else {
      // Format as day of week if within the next 7 days
      const dayDiff = Math.ceil((slotDate - today) / (1000 * 60 * 60 * 24))
      if (dayDiff <= 7) {
        dateText = slotDate.toLocaleDateString('en-US', { weekday: 'long' }).toLowerCase()
      } else {
        dateText = slotDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
      }
    }

    return `Available ${dateText} at ${timeSlot.time}`
  }

  // FeaturedCard component removed - using RegularCard for all doctors now

  const RegularCard = ({ doctor }) => (
    <Card
      style={{
        marginBottom: '16px',
        borderRadius: '12px',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
        opacity: isDoctorActive(doctor) ? 1 : 0.7,
        // Add gradient background for virtual doctor
        ...(doctor._id === 'virtual-doctor-24-7' && {
          background: 'linear-gradient(135deg, #3b82f6 0%, #06b6d4 100%)',
          border: 'none',
          boxShadow: '0 8px 32px rgba(59, 130, 246, 0.3)'
        })
      }}
      bodyStyle={{
        padding: '20px',
        // Make text white for virtual doctor on gradient background
        ...(doctor._id === 'virtual-doctor-24-7' && {
          color: 'white'
        })
      }}
    >
      <Row align='middle' gutter={20}>
        <Col flex='none'>
          <Avatar
            size={80}
            src={doctor.image || PlaceholderImage}
            icon={<UserOutlined />}
            style={{
              border:
                doctor._id === 'virtual-doctor-24-7'
                  ? '3px solid rgba(255, 255, 255, 0.8)'
                  : `3px solid ${token.colorPrimary}`
            }}
          />
        </Col>

        <Col flex='auto' style={{ minWidth: 0 }}>
          <Space direction='vertical' size='small' style={{ width: '100%' }}>
            <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
              <Title
                level={4}
                style={{
                  margin: 0,
                  color: doctor._id === 'virtual-doctor-24-7' ? 'white' : '#111827',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  maxWidth: '300px'
                }}
              >
                {getDoctorName(doctor)}
              </Title>
              {doctor._id === 'virtual-doctor-24-7' && (
                <Tag
                  style={{
                    background: 'rgba(255, 255, 255, 0.2)',
                    color: 'white',
                    border: '1px solid rgba(255, 255, 255, 0.3)',
                    fontWeight: '600',
                    fontSize: '11px',
                    padding: '2px 6px'
                  }}
                >
                  Our Picks!
                </Tag>
              )}
            </div>
            <Text
              type={doctor._id === 'virtual-doctor-24-7' ? undefined : 'secondary'}
              style={{
                color: doctor._id === 'virtual-doctor-24-7' ? 'rgba(255, 255, 255, 0.9)' : undefined,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                display: 'block',
                maxWidth: '100%'
              }}
            >
              {doctor.extraData.profession?.charAt(0).toUpperCase() +
                doctor.extraData.profession?.slice(1).toLowerCase()}
            </Text>

            <Space direction='vertical' size='small' style={{ width: '100%' }}>
              <Space size='small' style={{ width: '100%' }}>
                <EnvironmentOutlined
                  style={{
                    color: doctor._id === 'virtual-doctor-24-7' ? 'rgba(255, 255, 255, 0.8)' : 'red',
                    flexShrink: 0
                  }}
                />
                <Text
                  type={doctor._id === 'virtual-doctor-24-7' ? undefined : 'secondary'}
                  style={{
                    fontSize: '13px',
                    color: doctor._id === 'virtual-doctor-24-7' ? 'rgba(255, 255, 255, 0.8)' : undefined,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    flex: 1,
                    minWidth: 0
                  }}
                >
                  {doctor.clinicRecord?._id === 'virtual-doctor-24-7' ? (
                    'No address available'
                  ) : doctor.clinicRecord?.name ? (
                    <>
                      <u>{doctor.clinicRecord.name}</u>
                      {doctor.clinicRecord?.distanceFormatted && (
                        <span
                          style={{
                            marginLeft: '8px',
                            color: doctor._id === 'virtual-doctor-24-7' ? 'rgba(255, 255, 255, 0.6)' : '#52c41a',
                            fontWeight: '500',
                            fontSize: '12px'
                          }}
                        >
                          {searchType === 'virtual_consultation' ? (
                            'Virtual'
                          ) : (
                            <>• {doctor.clinicRecord.distanceFormatted}</>
                          )}
                        </span>
                      )}
                    </>
                  ) : doctor.residentialAddress ? (
                    doctor.residentialAddress
                  ) : (
                    'No address available'
                  )}
                </Text>
              </Space>
              <Space size='small'>
                <ThunderboltOutlined
                  style={{
                    color: doctor._id === 'virtual-doctor-24-7' ? 'rgba(255, 255, 255, 0.8)' : 'goldenrod'
                  }}
                />
                <Text
                  type={doctor._id === 'virtual-doctor-24-7' ? undefined : 'secondary'}
                  style={{
                    fontSize: '13px',
                    color: doctor._id === 'virtual-doctor-24-7' ? 'rgba(255, 255, 255, 0.8)' : undefined
                  }}
                >
                  Instant Booking
                </Text>
              </Space>
              <Space size='small'>
                <CalendarOutlined
                  style={{
                    color: doctor._id === 'virtual-doctor-24-7' ? 'rgba(255, 255, 255, 0.8)' : 'blue'
                  }}
                />
                <Text
                  type={doctor._id === 'virtual-doctor-24-7' ? undefined : 'secondary'}
                  style={{
                    fontSize: '13px',
                    color: doctor._id === 'virtual-doctor-24-7' ? 'rgba(255, 255, 255, 0.8)' : undefined
                  }}
                >
                  {doctor._id === 'virtual-doctor-24-7'
                    ? 'Available 24/7 - Instant Access'
                    : formatNextAvailableSlot(doctor.nextAvailableSlot)}
                </Text>
              </Space>
            </Space>
          </Space>
        </Col>

        <Col flex='none'>
          <Space direction='vertical' align='end' size='middle'>
            {/* <div style={{ textAlign: 'right' }}>
              <Rate
                disabled
                value={doctor.rating}
                style={{
                  fontSize: '14px',
                  color: doctor._id === 'virtual-doctor-24-7' ? 'rgba(255, 255, 255, 0.9)' : undefined
                }}
              />
              <div>
                <Text
                  type={doctor._id === 'virtual-doctor-24-7' ? undefined : 'secondary'}
                  style={{
                    fontSize: '12px',
                    color: doctor._id === 'virtual-doctor-24-7' ? 'rgba(255, 255, 255, 0.8)' : undefined
                  }}
                >
                  {doctor.reviewCount} Rating{doctor.reviewCount !== 1 ? 's' : ''}
                </Text>
              </div>
            </div> */}

            <Space>
              <Button
                type='text'
                icon={<MessageOutlined />}
                style={{
                  background: doctor._id === 'virtual-doctor-24-7' ? 'rgba(255, 255, 255, 0.2)' : '#f3f4f6',
                  color: doctor._id === 'virtual-doctor-24-7' ? 'rgba(255, 255, 255, 0.8)' : '#6b7280',
                  border: doctor._id === 'virtual-doctor-24-7' ? '1px solid rgba(255, 255, 255, 0.3)' : 'none'
                }}
              />
              <Button
                type='text'
                icon={<PhoneOutlined />}
                style={{
                  background: doctor._id === 'virtual-doctor-24-7' ? 'rgba(255, 255, 255, 0.2)' : '#f3f4f6',
                  color: doctor._id === 'virtual-doctor-24-7' ? 'rgba(255, 255, 255, 0.8)' : '#6b7280',
                  border: doctor._id === 'virtual-doctor-24-7' ? '1px solid rgba(255, 255, 255, 0.3)' : 'none'
                }}
              />
              <Button
                type='text'
                icon={<VideoCameraOutlined />}
                style={{
                  background: doctor._id === 'virtual-doctor-24-7' ? 'rgba(255, 255, 255, 0.2)' : '#f3f4f6',
                  color: doctor._id === 'virtual-doctor-24-7' ? 'rgba(255, 255, 255, 0.8)' : '#6b7280',
                  border: doctor._id === 'virtual-doctor-24-7' ? '1px solid rgba(255, 255, 255, 0.3)' : 'none'
                }}
              />
              <Button
                type='text'
                icon={<HomeOutlined />}
                style={{
                  background: doctor._id === 'virtual-doctor-24-7' ? 'rgba(255, 255, 255, 0.2)' : '#f3f4f6',
                  color: doctor._id === 'virtual-doctor-24-7' ? 'rgba(255, 255, 255, 0.8)' : '#6b7280',
                  border: doctor._id === 'virtual-doctor-24-7' ? '1px solid rgba(255, 255, 255, 0.3)' : 'none'
                }}
              />
            </Space>

            <Button
              type='primary'
              icon={<EyeOutlined />}
              onClick={() => {
                // Special handling for virtual doctor advertising
                if (doctor._id === 'virtual-doctor-24-7') {
                  // Add your virtual doctor platform redirect here
                  // window.open('https://your-virtual-doctor-platform.com', '_blank')
                } else {
                  handleViewProfile(doctor)
                }
              }}
              disabled={!isDoctorActive(doctor)}
              style={{
                background: doctor._id === 'virtual-doctor-24-7' ? 'rgba(255, 255, 255, 0.9)' : token.colorPrimary,
                borderColor: doctor._id === 'virtual-doctor-24-7' ? 'rgba(255, 255, 255, 0.9)' : token.colorPrimary,
                color: doctor._id === 'virtual-doctor-24-7' ? '#3b82f6' : 'white',
                fontWeight: '600',
                ...(doctor._id === 'virtual-doctor-24-7' && {
                  boxShadow: '0 4px 12px rgba(255, 255, 255, 0.3)'
                })
              }}
            >
              View Profile & Availability
            </Button>
          </Space>
        </Col>
      </Row>
    </Card>
  )

  if (loading || !showResults) {
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '60px 20px',
          minHeight: '400px',
          background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
          borderRadius: '16px',
          margin: '20px'
        }}
      >
        {/* Main Loading Animation */}
        <div
          style={{
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginBottom: '32px'
          }}
        >
          {/* Outer Rotating Ring */}
          <div
            style={{
              width: '80px',
              height: '80px',
              border: '3px solid rgba(59, 130, 246, 0.2)',
              borderTop: '3px solid #3b82f6',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
              position: 'absolute'
            }}
          />

          {/* Inner Pulsing Circle */}
          <div
            style={{
              width: '60px',
              height: '60px',
              background: 'linear-gradient(135deg, #3b82f6 0%, #06b6d4 100%)',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              animation: 'pulse 2s infinite',
              boxShadow: '0 8px 32px rgba(59, 130, 246, 0.4)'
            }}
          >
            <UserOutlined style={{ fontSize: '24px', color: 'white' }} />
          </div>
        </div>

        {/* Loading Text with Gradient */}
        <div style={{ textAlign: 'center', marginBottom: '24px' }}>
          <Title
            level={3}
            style={{
              background: 'linear-gradient(135deg, #3b82f6 0%, #06b6d4 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              margin: '0 0 8px 0',
              fontWeight: '600'
            }}
          >
            Finding Your Perfect Doctor
          </Title>
          <Text style={{ color: '#6b7280', fontSize: '16px' }}>
            Searching through our network of healthcare professionals...
          </Text>
        </div>

        {/* Search Progress Indicators */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', width: '100%', maxWidth: '400px' }}>
          {[
            // Only show location analysis for non-virtual consultations
            ...(searchType !== 'virtual_consultation'
              ? [{ icon: <EnvironmentOutlined />, text: 'Analyzing your location...', delay: '0s' }]
              : []),
            {
              icon: <UserOutlined />,
              text: 'Matching with available doctors...',
              delay: searchType !== 'virtual_consultation' ? '0.5s' : '0s'
            },
            {
              icon: <CalendarOutlined />,
              text: 'Checking appointment availability...',
              delay: searchType !== 'virtual_consultation' ? '1s' : '0.5s'
            },
            {
              icon: <ThunderboltOutlined />,
              text: 'Finalizing your results...',
              delay: searchType !== 'virtual_consultation' ? '1.5s' : '1s'
            }
          ].map((step, index) => (
            <div
              key={index}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                padding: '12px 16px',
                backgroundColor: 'rgba(255, 255, 255, 0.8)',
                borderRadius: '8px',
                backdropFilter: 'blur(10px)',
                animation: `fadeInUp 0.6s ease-out ${step.delay} both`,
                border: '1px solid rgba(255, 255, 255, 0.3)'
              }}
            >
              <div
                style={{
                  width: '32px',
                  height: '32px',
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, #3b82f6 0%, #06b6d4 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  fontSize: '14px',
                  animation: 'bounce 2s infinite'
                }}
              >
                {step.icon}
              </div>
              <Text style={{ color: '#374151', fontSize: '14px', fontWeight: '500' }}>{step.text}</Text>
              <div style={{ marginLeft: 'auto' }}>
                <div
                  style={{
                    width: '16px',
                    height: '16px',
                    border: '2px solid rgba(59, 130, 246, 0.2)',
                    borderTop: '2px solid #3b82f6',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite'
                  }}
                />
              </div>
            </div>
          ))}
        </div>

        {/* Add CSS animations */}
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
          
          @keyframes pulse {
            0%, 100% { transform: scale(1); }
            50% { transform: scale(1.1); }
          }
          
          @keyframes fadeInUp {
            from {
              opacity: 0;
              transform: translateY(20px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
          
          @keyframes bounce {
            0%, 20%, 50%, 80%, 100% { transform: translateY(0); }
            40% { transform: translateY(-4px); }
            60% { transform: translateY(-2px); }
          }
        `}</style>
      </div>
    )
  }

  // Fixed 24/7 Virtual Doctor - Always shown as advertising space
  // const virtualDoctor = {
  //   _id: 'virtual-doctor-24-7',
  //   firstName: 'Online Doctor',
  //   lastName: '(24/7)',
  //   specialty: 'General Practitioner',
  //   practice: 'Meta-Clinic Virtual Healthcare',
  //   rating: 4.8,
  //   reviewCount: 33,
  //   image: '/api/placeholder/100/100',
  //   isVirtual: true,
  //   isInstantBooking: true,
  //   availability: 'Available 24/7 - Instant Access',
  //   extraData: {
  //     qualification: 'General Practitioner',
  //     isActive: true
  //   }
  // }

  return (
    <div
      style={{
        maxWidth: '1200px',
        margin: '0 auto',
        padding: '20px'
      }}
    >
      {/* Fixed 24/7 Virtual Doctor Advertising Space - Always at top */}
      {/* {showResults && (
        <div style={{ marginBottom: '16px' }}>
          <RegularCard doctor={virtualDoctor} />
        </div>
      )} */}

      {/* Search Results - All doctors shown as regular cards */}
      {displayResults.length === 0 && !loading && showResults && (
        <div style={{ marginBottom: '20px' }}>
          {searchFilters.location ? (
            // Location-based search with no results
            <Card
              style={{
                borderRadius: '16px',
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
                textAlign: 'center',
                background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
                border: 'none'
              }}
              bodyStyle={{ padding: '48px 24px' }}
            >
              <div style={{ marginBottom: '24px' }}>
                <div
                  style={{
                    width: '80px',
                    height: '80px',
                    borderRadius: '50%',
                    background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto 20px auto',
                    boxShadow: '0 8px 24px rgba(245, 158, 11, 0.3)'
                  }}
                >
                  <EnvironmentOutlined style={{ fontSize: '32px', color: 'white' }} />
                </div>
                <Title level={3} style={{ color: '#92400e', marginBottom: '12px', fontWeight: '600' }}>
                  No doctors found in your area
                </Title>
                <Text style={{ fontSize: '16px', color: '#a16207', display: 'block', marginBottom: '20px' }}>
                  We couldn't find any doctors within {searchFilters.maxDistance || 50}km of your location
                </Text>
              </div>

              <div style={{ marginBottom: '24px' }}>
                <Text style={{ fontSize: '14px', color: '#a16207', display: 'block', marginBottom: '12px' }}>
                  📍 <strong>Current search area:</strong> {searchFilters.location?.address || 'Your selected location'}
                </Text>
                <Text style={{ fontSize: '14px', color: '#a16207', display: 'block' }}>
                  🔍 <strong>Search radius:</strong> {searchFilters.maxDistance || 50}km
                </Text>
              </div>

              <div
                style={{
                  background: 'rgba(255, 255, 255, 0.8)',
                  borderRadius: '12px',
                  padding: '20px',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(255, 255, 255, 0.3)'
                }}
              >
                <Text strong style={{ fontSize: '14px', color: '#92400e', display: 'block', marginBottom: '12px' }}>
                  💡 Try these suggestions:
                </Text>
                <div style={{ textAlign: 'left', color: '#a16207' }}>
                  <div style={{ marginBottom: '8px' }}>• Increase your search radius to 75km or 100km</div>
                  <div style={{ marginBottom: '8px' }}>• Try searching in a nearby city or town</div>
                  <div style={{ marginBottom: '8px' }}>• Remove some filters to see more results</div>
                  <div>• Consider virtual consultations (available 24/7)</div>
                </div>
              </div>
            </Card>
          ) : (
            // General search with no results
            <Card
              style={{
                borderRadius: '16px',
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
                textAlign: 'center',
                background: 'linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%)',
                border: 'none'
              }}
              bodyStyle={{ padding: '48px 24px' }}
            >
              <div style={{ marginBottom: '24px' }}>
                <div
                  style={{
                    width: '80px',
                    height: '80px',
                    borderRadius: '50%',
                    background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto 20px auto',
                    boxShadow: '0 8px 24px rgba(59, 130, 246, 0.3)'
                  }}
                >
                  <UserOutlined style={{ fontSize: '32px', color: 'white' }} />
                </div>
                <Title level={3} style={{ color: '#1e40af', marginBottom: '12px', fontWeight: '600' }}>
                  No doctors match your search
                </Title>
                <Text style={{ fontSize: '16px', color: '#3730a3', display: 'block', marginBottom: '20px' }}>
                  We couldn't find any doctors matching your current search criteria
                </Text>
              </div>

              {/* Show active filters if any */}
              {(searchFilters.consultType?.length > 0 ||
                searchFilters.timeSlot ||
                searchFilters.languages?.length > 0 ||
                searchFilters.gender) && (
                <div
                  style={{
                    background: 'rgba(255, 255, 255, 0.8)',
                    borderRadius: '12px',
                    padding: '20px',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(255, 255, 255, 0.3)',
                    marginBottom: '24px'
                  }}
                >
                  <Text strong style={{ fontSize: '14px', color: '#1e40af', display: 'block', marginBottom: '12px' }}>
                    🔍 Your current filters:
                  </Text>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', justifyContent: 'center' }}>
                    {searchFilters.consultType?.map((type) => (
                      <span
                        key={type}
                        style={{
                          background: `${getFilterColor('consultType', type)}15`,
                          color: getFilterColor('consultType', type),
                          padding: '4px 8px',
                          borderRadius: '6px',
                          fontSize: '12px',
                          border: `1px solid ${getFilterColor('consultType', type)}30`,
                          fontWeight: '500'
                        }}
                      >
                        {formatFilterValue('consultType', type)}
                      </span>
                    ))}
                    {searchFilters.timeSlot && (
                      <span
                        style={{
                          background: `${getFilterColor('timeSlot')}15`,
                          color: getFilterColor('timeSlot'),
                          padding: '4px 8px',
                          borderRadius: '6px',
                          fontSize: '12px',
                          border: `1px solid ${getFilterColor('timeSlot')}30`,
                          fontWeight: '500'
                        }}
                      >
                        {formatFilterValue('timeSlot', searchFilters.timeSlot)}
                      </span>
                    )}
                    {searchFilters.languages?.map((lang) => (
                      <span
                        key={lang}
                        style={{
                          background: `${getFilterColor('languages')}15`,
                          color: getFilterColor('languages'),
                          padding: '4px 8px',
                          borderRadius: '6px',
                          fontSize: '12px',
                          border: `1px solid ${getFilterColor('languages')}30`,
                          fontWeight: '500'
                        }}
                      >
                        {formatFilterValue('languages', lang)}
                      </span>
                    ))}
                    {searchFilters.gender && (
                      <span
                        style={{
                          background: `${getFilterColor('gender')}15`,
                          color: getFilterColor('gender'),
                          padding: '4px 8px',
                          borderRadius: '6px',
                          fontSize: '12px',
                          border: `1px solid ${getFilterColor('gender')}30`,
                          fontWeight: '500'
                        }}
                      >
                        {formatFilterValue('gender', searchFilters.gender)}
                      </span>
                    )}
                  </div>
                </div>
              )}

              <div
                style={{
                  background: 'rgba(255, 255, 255, 0.8)',
                  borderRadius: '12px',
                  padding: '20px',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(255, 255, 255, 0.3)'
                }}
              >
                <Text strong style={{ fontSize: '14px', color: '#1e40af', display: 'block', marginBottom: '12px' }}>
                  💡 Try these suggestions:
                </Text>
                <div style={{ textAlign: 'left', color: '#3730a3' }}>
                  <div style={{ marginBottom: '8px' }}>• Remove some filters to broaden your search</div>
                  <div style={{ marginBottom: '8px' }}>• Try a different medical specialty</div>
                  <div style={{ marginBottom: '8px' }}>• Add a location to find nearby doctors</div>
                  <div>• Consider our 24/7 virtual consultation option</div>
                </div>
              </div>
            </Card>
          )}
        </div>
      )}

      {showResults && displayResults.map((doctor) => <RegularCard key={doctor._id} doctor={doctor} />)}
    </div>
  )
}

export default SearchResults
